import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import { ResponsiveBar } from "@nivo/bar";
import { useGetTotalEmissions } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { useGetOrganizationStructure } from "../../../../hooks/query";
import { useGetCompanyDimensionReports } from "../../../../hooks/query/reports";
import LocalSpinner from "../../../../components/LocalSpinner";
import { chartTheme, makeBarTooltip } from "./Consumption/chartTheme";
import { YearStartEndDropdown } from "../supplier-components/SupplierTrends";

// Intensity view — compare % change in consumption vs % change in emissions
// per sub-category (Scope 1: Stationary / Mobile / Refrigerant) or per
// electricity type (Scope 2). Matches the scope options on the Change tab.

const SHOW_OPTIONS = [
  { label: "Scope 1", value: "scope1" },
  { label: "Scope 2", value: "scope2" },
];

const SCOPE1_SUBCATEGORIES = [
  { key: "stationary", label: "Stationary" },
  { key: "mobile", label: "Mobile" },
  { key: "refrigerant", label: "Refrigerant" },
];

// Scope 2 v2 purchased-electricity categories (ReportElectricityV2Item
// category codes). Same labels as ScopeTwoDetail / WaterfallChart.
const SCOPE2_V2_LABELS = {
  STANDARD_GRID: "Standard Grid",
  SUPPLIER_CONTRACTED: "Supplier Contracted",
  CERTIFIED_RENEWABLE: "Certified Renewable",
  DIRECT_PRIVATE_WIRE: "Direct / Private Wire",
  ONSITE: "On-site generation",
};

const DIMENSION_LEVEL_KEYS = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"];

// Parse the FootPrint dimensionParams string ("dimensionOne=1,2&dimensionTwo=3")
// back into per-level request params for the by-dimension reports endpoint.
const parseDimensionParams = (str) =>
  Object.fromEntries(
    (str || "")
      .split("&")
      .filter(Boolean)
      .map((pair) => pair.split("="))
      .filter(([k, v]) => DIMENSION_LEVEL_KEYS.includes(k) && v)
  );

// Sum a year's per-dimension-item electricityV2 rows into per-category
// location-based emissions + kWh totals.
const aggregateV2Categories = (reportContent) => {
  const byCategory = {};
  (reportContent || []).forEach((row) => {
    (row.electricityV2 || []).forEach((c) => {
      const k = c.category;
      if (!byCategory[k]) byCategory[k] = { emissions: 0, kwh: 0 };
      byCategory[k].emissions += Number(c.locationCo2Emissions) || 0;
      byCategory[k].kwh += Number(c.consumptionKwh) || 0;
    });
  });
  return byCategory;
};

const METRIC_COLORS = {
  consumption: "#5EB5F3",
  emissions: "#9A34F3",
};

const pct = (current, prior) => {
  if (typeof current !== "number" || typeof prior !== "number" || prior === 0) return null;
  return ((current - prior) / prior) * 100;
};

// "ELECTRICITY_LOCATION_BASED" → "Electricity Location Based"
const prettyLabel = (value) =>
  String(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function FilterDropdown({ options, selected, onSelect, prefix }) {
  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
      >
        {prefix}: {selected.label}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => onSelect(opt)}
            fontWeight={selected.value === opt.value ? "bold" : "normal"}
          >
            {opt.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

function ConsumptionVsEmissionsChart({ selectedYear, dimensionParams, quarter }) {
  const defaultTo = selectedYear || new Date().getFullYear();
  const [startYear, setStartYear] = useState(defaultTo - 1);
  const [endYear, setEndYear] = useState(defaultTo);
  const [showScope, setShowScope] = useState(SHOW_OPTIONS[0]);

  // Follow page year unless user adjusted the range manually.
  const userOverrodeYears = useRef(false);
  useEffect(() => {
    if (userOverrodeYears.current) return;
    if (!selectedYear) return;
    setEndYear(selectedYear);
    setStartYear(selectedYear - 1);
  }, [selectedYear]);

  const handleSetStartYear = (y) => { userOverrodeYears.current = true; setStartYear(y); };
  const handleSetEndYear = (y) => { userOverrodeYears.current = true; setEndYear(y); };

  const invalidRange = endYear <= startYear;

  const { data: curr, isFetching: fetchingCurr } = useGetTotalEmissions(endYear, dimensionParams, {
    cacheTime: 60_000,
    enabled: !invalidRange,
    retry: false,
  }, quarter);
  const { data: prev, isFetching: fetchingPrev } = useGetTotalEmissions(startYear, dimensionParams, {
    cacheTime: 60_000,
    enabled: !invalidRange,
    retry: false,
  }, quarter);
  // Scope 2 v2 orgs have no rows on scopeOneAndTwoDetail.electricity — their
  // per-category kWh + emissions live on the by-dimension report rows
  // (electricityV2, same source ScopeTwoDetail/WaterfallChart aggregate).
  const { data: organizationStructure } = useGetOrganizationStructure();
  const dimensionFilter = useMemo(() => parseDimensionParams(dimensionParams), [dimensionParams]);
  const v2DimensionId = useMemo(() => {
    const configuredKeys = DIMENSION_LEVEL_KEYS.filter(
      (k) => organizationStructure?.dimensions?.[k]?.id && organizationStructure?.dimensions?.[k]?.name
    );
    if (configuredKeys.length === 0) return null;
    const selectedKeys = DIMENSION_LEVEL_KEYS.filter((k) => dimensionFilter[k]);
    const currentKey = selectedKeys.length > 0 ? selectedKeys[selectedKeys.length - 1] : null;
    const nextKey = configuredKeys[currentKey ? configuredKeys.indexOf(currentKey) + 1 : 0];
    return organizationStructure?.dimensions?.[nextKey]?.id || null;
  }, [organizationStructure, dimensionFilter]);

  const isScopeTwo = showScope.value === "scope2";
  const reportQuarter = quarter || "ALL";
  const { data: currV2Report, isFetching: fetchingCurrV2 } = useGetCompanyDimensionReports(
    v2DimensionId,
    { year: endYear, quarter: reportQuarter, page: 0, size: 200, ...dimensionFilter },
    { enabled: isScopeTwo && !invalidRange && !!v2DimensionId && !!endYear }
  );
  const { data: prevV2Report, isFetching: fetchingPrevV2 } = useGetCompanyDimensionReports(
    v2DimensionId,
    { year: startYear, quarter: reportQuarter, page: 0, size: 200, ...dimensionFilter },
    { enabled: isScopeTwo && !invalidRange && !!v2DimensionId && !!startYear }
  );

  const isFetching =
    fetchingCurr || fetchingPrev || (isScopeTwo && (fetchingCurrV2 || fetchingPrevV2));

  // Build [{ label, emissionsPct, consumptionPct }] rows — one per Scope 1
  // sub-category, or one per electricity type for Scope 2.
  const rows = useMemo(() => {
    if (!curr || !prev) return [];

    // Helpers shared by both scopes. Grouping by (consumptionType,
    // measurementUnit) is the only unit-homogeneous basis for a real consumption
    // % — a sub-category like "Stationary" mixes litres, kWh, kg, etc., so its
    // raw consumption can't be summed. Single unit: exact % on the summed
    // totals. Mixed units: per-group % changes blended, weighted by each
    // group's prior-year emissions (percentages are unitless, so the blend
    // is meaningful where a raw sum would not be).
    const sumEmissions = (items) =>
      (items || []).reduce((acc, i) => acc + (Number(i.emissions) || 0), 0);

    const consumptionPctFor = (currItems, prevItems) => {
      const groups = {};
      const accumulate = (yearKey, items) => {
        (items || []).forEach((item) => {
          const key = `${item.consumptionType || "Unspecified"}|${item.measurementUnit || ""}`;
          if (!groups[key]) {
            groups[key] = { unit: item.measurementUnit || "", currCons: 0, prevCons: 0, prevEm: 0 };
          }
          groups[key][yearKey === "curr" ? "currCons" : "prevCons"] += Number(item.consumption) || 0;
          if (yearKey === "prev") groups[key].prevEm += Number(item.emissions) || 0;
        });
      };
      accumulate("curr", currItems);
      accumulate("prev", prevItems);

      const list = Object.values(groups);
      if (list.length === 0) return null;
      if (new Set(list.map((g) => g.unit)).size === 1) {
        return pct(
          list.reduce((acc, g) => acc + g.currCons, 0),
          list.reduce((acc, g) => acc + g.prevCons, 0)
        );
      }
      const weighted = list
        .map((g) => ({ p: pct(g.currCons, g.prevCons), w: g.prevEm }))
        .filter((g) => g.p !== null && g.w > 0);
      const totalW = weighted.reduce((acc, g) => acc + g.w, 0);
      if (totalW === 0) return null;
      return weighted.reduce((acc, g) => acc + g.p * (g.w / totalW), 0);
    };

    if (showScope.value === "scope1") {
      return SCOPE1_SUBCATEGORIES.map(({ key, label }) => {
        const currItems = curr.scopeOneAndTwoDetail?.[key] || [];
        const prevItems = prev.scopeOneAndTwoDetail?.[key] || [];
        return {
          label,
          emissionsPct: pct(sumEmissions(currItems), sumEmissions(prevItems)),
          consumptionPct: consumptionPctFor(currItems, prevItems),
        };
      }).filter((r) => r.emissionsPct !== null || r.consumptionPct !== null);
    }

    // Scope 2 v2 orgs: per-category kWh + location-based emissions from the
    // by-dimension reports (kWh throughout, so consumption % is exact).
    const currV2 = aggregateV2Categories(currV2Report?.content);
    const prevV2 = aggregateV2Categories(prevV2Report?.content);
    const hasV2 = Object.keys(currV2).length > 0 || Object.keys(prevV2).length > 0;
    if (hasV2) {
      return Object.keys(SCOPE2_V2_LABELS)
        .map((cat) => ({
          label: SCOPE2_V2_LABELS[cat],
          emissionsPct: pct(currV2[cat]?.emissions ?? 0, prevV2[cat]?.emissions ?? 0),
          consumptionPct: pct(currV2[cat]?.kwh ?? 0, prevV2[cat]?.kwh ?? 0),
        }))
        .filter((r) => r.emissionsPct !== null || r.consumptionPct !== null)
        .sort((a, b) => Math.abs(b.emissionsPct ?? 0) - Math.abs(a.emissionsPct ?? 0));
    }

    // Scope 2 v1 orgs: one row per electricity type on the flat electricity
    // list. All rows are kWh, so consumption sums directly within each type.
    const byType = {};
    const collect = (yearKey, items) => {
      (items || []).forEach((item) => {
        const type = item.consumptionType || "Unspecified";
        if (!byType[type]) byType[type] = { curr: [], prev: [] };
        byType[type][yearKey].push(item);
      });
    };
    collect("curr", curr.scopeOneAndTwoDetail?.electricity);
    collect("prev", prev.scopeOneAndTwoDetail?.electricity);

    return Object.entries(byType)
      .map(([type, items]) => ({
        label: prettyLabel(type),
        emissionsPct: pct(sumEmissions(items.curr), sumEmissions(items.prev)),
        consumptionPct: consumptionPctFor(items.curr, items.prev),
      }))
      .filter((r) => r.emissionsPct !== null || r.consumptionPct !== null)
      .sort((a, b) => Math.abs(b.emissionsPct ?? 0) - Math.abs(a.emissionsPct ?? 0));
  }, [curr, prev, currV2Report, prevV2Report, showScope]);

  const barData = useMemo(() => (
    rows.map((r) => ({
      label: r.label,
      Consumption: r.consumptionPct ?? 0,
      Emissions: r.emissionsPct ?? 0,
      _rawConsumption: r.consumptionPct,
      _rawEmissions: r.emissionsPct,
    }))
  ), [rows]);

  const hasAny = barData.length > 0;
  // A row's consumptionPct can be null (e.g. no prior-year rows to compare).
  // Only render the Consumption series/legend when at least one row has it.
  const hasConsumption = rows.some((r) => r.consumptionPct != null);

  const legendItems = [
    ...(hasConsumption ? [{ id: "Consumption", color: METRIC_COLORS.consumption }] : []),
    { id: "Emissions", color: METRIC_COLORS.emissions },
  ];

  // Height scales with row count so longer lists don't get crushed.
  const chartHeight = Math.max(300, barData.length * 48);

  return (
    <Flex direction="column" gap={4}>
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <YearStartEndDropdown
            setStartYear={handleSetStartYear}
            startYear={startYear}
            setEndYear={handleSetEndYear}
            endYear={endYear}
          />
          <FilterDropdown options={SHOW_OPTIONS} selected={showScope} onSelect={setShowScope} prefix="Show" />
        </HStack>
        <HStack spacing={4}>
          {legendItems.map((item) => (
            <HStack key={item.id} spacing={2}>
              <Box w="10px" h="10px" borderRadius="full" bg={item.color} />
              <Text fontSize="body.xs" fontWeight="600" color="gray.600">{item.id}</Text>
            </HStack>
          ))}
        </HStack>
      </Flex>

      <Box>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          {showScope.value === "scope1"
            ? "Consumption vs emissions — by sub-category"
            : "Consumption vs emissions — by electricity type"}
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          {`% change in activity (consumption) alongside % change in CO₂e per ${
            showScope.value === "scope1" ? "sub-category" : "electricity type"
          } · ${showScope.label} · ${startYear} → ${endYear}.`}
        </Text>
      </Box>

      {invalidRange && (
        <Flex align="center" justify="center" h="300px" color="gray.400">
          <Text>End year must be after start year.</Text>
        </Flex>
      )}

      {!invalidRange && isFetching && <LocalSpinner />}

      {!invalidRange && !isFetching && !hasAny && (
        <Flex align="center" justify="center" h="300px" color="gray.400">
          <Text>Not enough data to compare {startYear} and {endYear}.</Text>
        </Flex>
      )}

      {!invalidRange && !isFetching && hasAny && (
        <Box h={`${chartHeight}px`}>
          <ResponsiveBar
            data={barData}
            keys={hasConsumption ? ["Consumption", "Emissions"] : ["Emissions"]}
            indexBy="label"
            groupMode="grouped"
            layout="horizontal"
            margin={{ top: 16, right: 56, bottom: 60, left: 160 }}
            padding={0.25}
            innerPadding={4}
            colors={({ id }) => METRIC_COLORS[id.toLowerCase()] || "#999"}
            valueFormat={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              legend: "% change vs prior year",
              legendPosition: "middle",
              legendOffset: 40,
              format: (v) => `${v >= 0 ? "+" : ""}${v}%`,
            }}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            enableLabel={false}
            borderRadius={2}
            markers={[
              { axis: "x", value: 0, lineStyle: { stroke: "#CBD5E0", strokeWidth: 1 } },
            ]}
            theme={chartTheme}
            tooltip={makeBarTooltip(null, "%")}
          />
        </Box>
      )}

      <Text fontSize="body.xs" color="gray.500">
        Bars extending right show an increase; left shows a decrease. When the
        emissions bar moves further than consumption in the same direction, the
        intensity of that row has worsened; the opposite suggests
        decarbonisation is outpacing the change in activity.
      </Text>
    </Flex>
  );
}

export default ConsumptionVsEmissionsChart;
